import { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { GraduationCap, Plus, Trash2, Upload, FileText, ChevronRight, ChevronLeft, Loader2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { toTitleCase } from "@/lib/utils";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8000").replace(/\/$/, "");
const API = `${BACKEND_URL}/api`;

const academicLevels = [
  { value: "tecnica", label: "Carrera Técnica" },
  { value: "tecnologica", label: "Carrera Tecnológica" },
  { value: "universitaria", label: "Título Universitario" },
  { value: "especializacion", label: "Especialización" },
  { value: "maestria", label: "Maestría" },
  { value: "doctorado", label: "Doctorado" },
  { value: "curso", label: "Curso / Certificación" },
];


const requiredCertificateLevels = new Set([
  "tecnica",
  "tecnologica",
  "universitaria",
  "especializacion",
  "maestria",
  "doctorado",
  "curso",
]);

const baseTitleOptions = [
  "Bachiller",
  "Técnico",
  "Tecnólogo",
  "Profesional",
  "Especialista",
  "Magíster",
  "Doctor",
  "Diplomado",
  "Curso",
  "Certificación",
];

const commonBranches = [
  "Administración",
  "Ingeniería Y Tecnología",
  "Salud",
  "Industrial",
  "Construcción",
  "Agropecuario",
  "Turismo Y Servicios",
  "General",
  "Contabilidad Y Finanzas",
  "Mercadeo Y Ventas",
  "Logística Y Transporte",
  "Educación",
  "Derecho",
  "Ciencias Sociales",
  "Artes Y Diseño",
  "Comunicación",
];

const branchOptionsByLevel = {
  tecnica: commonBranches,
  tecnologica: commonBranches,
  universitaria: commonBranches,
  especializacion: commonBranches,
  maestria: commonBranches,
  doctorado: commonBranches,
  curso: commonBranches,
};

const titleSuffixesByBranch = {
  "Administración": [
    "Administración",
    "Gestión Empresarial",
    "Contabilidad",
    "Finanzas",
    "Talento Humano",
    "Mercadeo",
    "Ventas",
    "Logística",
    "Compras",
    "Servicio Al Cliente",
  ],
  "Ingeniería Y Tecnología": [
    "Ingeniería De Sistemas",
    "Ingeniería Industrial",
    "Ingeniería Civil",
    "Ingeniería Mecánica",
    "Ingeniería Electrónica",
    "Ingeniería Ambiental",
    "Desarrollo De Software",
    "Redes Y Telecomunicaciones",
    "Seguridad Informática",
    "Analítica De Datos",
  ],
  "Salud": [
    "Enfermería",
    "Salud Pública",
    "Seguridad Y Salud En El Trabajo",
    "Laboratorio Clínico",
    "Farmacia",
    "Fisioterapia",
    "Radiología",
    "Psicología",
    "Nutrición",
    "Odontología",
  ],
  "Industrial": [
    "Producción",
    "Mantenimiento Industrial",
    "Gestión De Calidad",
    "Procesos Industriales",
    "Manufactura",
    "Seguridad Industrial",
    "Operaciones",
    "Logística Industrial",
    "Metrología",
    "Gestión De Planta",
  ],
  "Construcción": [
    "Construcción",
    "Obras Civiles",
    "Topografía",
    "Dibujo Técnico",
    "Materiales De Construcción",
    "Interventoría",
    "Seguridad En Obra",
    "Arquitectura",
    "Estructuras",
    "Gestión De Proyectos De Construcción",
  ],
  "Agropecuario": [
    "Producción Agrícola",
    "Zootecnia",
    "Agroindustria",
    "Gestión Ambiental",
    "Agronomía",
    "Riego Y Drenaje",
    "Manejo De Cultivos",
    "Producción Animal",
    "Agroecología",
    "Suelos Y Fertilidad",
  ],
  "Turismo Y Servicios": [
    "Turismo",
    "Hotelería",
    "Gastronomía",
    "Servicios",
    "Guías Turísticos",
    "Agencia De Viajes",
    "Gestión Hotelera",
    "Servicio Al Cliente",
    "Eventos",
    "Barismo",
  ],
  "General": [
    "Gestión Administrativa",
    "Ofimática",
    "Servicio Al Cliente",
    "Auxiliar Administrativo",
    "Gestión Documental",
    "Habilidades Digitales",
    "Seguridad Y Salud En El Trabajo",
    "Emprendimiento",
    "Liderazgo",
    "Comunicación Efectiva",
  ],
  "Contabilidad Y Finanzas": [
    "Contabilidad",
    "Finanzas",
    "Auditoría",
    "Tributaria",
    "Costos",
    "Tesorería",
    "Presupuesto",
    "Análisis Financiero",
    "Control Interno",
    "Gestión Financiera",
  ],
  "Mercadeo Y Ventas": [
    "Mercadeo",
    "Marketing Digital",
    "Ventas",
    "Comercio",
    "Servicio Al Cliente",
    "Publicidad",
    "Investigación De Mercados",
    "Gestión Comercial",
    "Comercio Electrónico",
    "Branding",
  ],
  "Logística Y Transporte": [
    "Logística",
    "Transporte",
    "Distribución",
    "Almacenamiento",
    "Cadena De Suministro",
    "Compras Y Abastecimiento",
    "Comercio Exterior",
    "Aduanas",
    "Inventarios",
    "Gestión De Flotas",
  ],
  "Educación": [
    "Educación",
    "Pedagogía",
    "Docencia",
    "Educación Infantil",
    "Educación Especial",
    "Educación Virtual",
    "Gestión Educativa",
    "Didáctica",
    "Educación Inclusiva",
    "Orientación Escolar",
  ],
  "Derecho": [
    "Derecho",
    "Derecho Laboral",
    "Derecho Administrativo",
    "Derecho Penal",
    "Derecho Civil",
    "Derecho Comercial",
    "Derecho Constitucional",
    "Derecho Público",
    "Derecho Privado",
    "Derecho Internacional",
  ],
  "Ciencias Sociales": [
    "Ciencias Sociales",
    "Trabajo Social",
    "Sociología",
    "Antropología",
    "Comunicación Social",
    "Psicología Social",
    "Políticas Públicas",
    "Desarrollo Social",
    "Geografía Humana",
    "Historia",
  ],
  "Artes Y Diseño": [
    "Diseño Gráfico",
    "Diseño Industrial",
    "Diseño De Interiores",
    "Moda",
    "Multimedia",
    "Fotografía",
    "Ilustración",
    "Artes Visuales",
    "Música",
    "Producción Audiovisual",
  ],
  "Comunicación": [
    "Comunicación",
    "Periodismo",
    "Comunicación Digital",
    "Publicidad",
    "Relaciones Públicas",
    "Producción Audiovisual",
    "Comunicación Corporativa",
    "Comunicación Social",
    "Locución",
    "Gestión De Medios",
  ],
};

const titlePrefixByLevel = {
  tecnica: "Técnico En ",
  tecnologica: "Tecnólogo En ",
  universitaria: "Profesional En ",
  especializacion: "Especialista En ",
  maestria: "Maestría En ",
  doctorado: "Doctorado En ",
  curso: "Curso De ",
};

const titleOptionsByLevelAndBranch = Object.keys(titlePrefixByLevel).reduce(
  (acc, level) => {
    const prefix = titlePrefixByLevel[level];
    acc[level] = commonBranches.reduce((branchAcc, branch) => {
      const suffixes = titleSuffixesByBranch[branch] || [];
      branchAcc[branch] = suffixes.map((suffix) => `${prefix}${suffix}`);
      return branchAcc;
    }, {});
    return acc;
  },
  {}
);

const formSchema = z.object({
  level: z.string().min(1, "Seleccione Un Nivel Académico"),
  branch: z.string().min(2, "Seleccione Una Rama"),
  title: z.string().min(2, "El Título Es Requerido"),
  year: z.string().min(4, "El Año Es Requerido"),
});

const mergeUniqueOptions = (...optionGroups) => {
  const seen = new Set();
  const merged = [];
  optionGroups.flat().forEach((value) => {
    if (!value || typeof value !== "string") return;
    const cleaned = value.trim();
    if (!cleaned) return;
    const normalized = cleaned.toLocaleLowerCase("es");
    if (normalized === "otro") return;
    if (seen.has(normalized)) return;
    seen.add(normalized);
    merged.push(cleaned);
  });
  return merged;
};

export default function AcademicInfoForm({ profile, profileId, setProfile, onNext, onPrev, loading }) {
  const [entries, setEntries] = useState(profile?.academic_info || []);
  const [isAdding, setIsAdding] = useState(false);
  const [uploadingCert, setUploadingCert] = useState(false);
  const [certUrl, setCertUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [branchOption, setBranchOption] = useState("");
  const [customBranch, setCustomBranch] = useState("");
  const [titleOption, setTitleOption] = useState("");
  const [customTitle, setCustomTitle] = useState("");
  const [dynamicBranchOptions, setDynamicBranchOptions] = useState([]);
  const [dynamicTitleOptions, setDynamicTitleOptions] = useState([]);
  const [yearOption, setYearOption] = useState("");
  const fileInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      level: "",
      branch: "",
      title: "",
      year: "",
    },
  });

  const selectedLevel = watch("level");
  const selectedBranch = watch("branch");
  const graduationYears = Array.from({ length: 60 }, (_, index) => {
    const year = new Date().getFullYear() - index;
    return String(year);
  });


  const isLevelSelected = Boolean(selectedLevel);
  const staticBranchOptions = isLevelSelected ? branchOptionsByLevel[selectedLevel] || [] : [];
  const availableBranchOptions = mergeUniqueOptions(staticBranchOptions, dynamicBranchOptions);
  const isBranchSelected = Boolean(selectedBranch);
  const branchPlaceholder = isLevelSelected ? "Seleccione Una Rama" : "Seleccione El Nivel Primero";
  const titlePlaceholder = isBranchSelected ? "Seleccione Un Título" : "Seleccione La Rama Primero";
  const staticTitleOptions = isBranchSelected
    ? titleOptionsByLevelAndBranch[selectedLevel]?.[selectedBranch] || baseTitleOptions
    : [];
  const availableTitleOptions = isBranchSelected
    ? mergeUniqueOptions(staticTitleOptions, dynamicTitleOptions)
    : [];
  const isCertificateRequired = requiredCertificateLevels.has(selectedLevel);

  useEffect(() => {
    const loadBranchOptions = async () => {
      if (!selectedLevel) {
        setDynamicBranchOptions([]);
        return;
      }

      try {
        const response = await axios.get(`${API}/academic/options`, {
          params: { level: selectedLevel },
        });
        const branches = Array.isArray(response.data?.branches) ? response.data.branches : [];
        setDynamicBranchOptions(branches);
      } catch (error) {
        console.error("Error loading dynamic academic branch options:", error);
        setDynamicBranchOptions([]);
      }
    };

    loadBranchOptions();
  }, [selectedLevel]);

  useEffect(() => {
    const loadTitleOptions = async () => {
      if (!selectedLevel || !selectedBranch || branchOption === "otro") {
        setDynamicTitleOptions([]);
        return;
      }

      try {
        const response = await axios.get(`${API}/academic/options`, {
          params: {
            level: selectedLevel,
            branch: selectedBranch,
          },
        });
        const titles = Array.isArray(response.data?.titles) ? response.data.titles : [];
        setDynamicTitleOptions(titles);
      } catch (error) {
        console.error("Error loading dynamic academic title options:", error);
        setDynamicTitleOptions([]);
      }
    };

    loadTitleOptions();
  }, [branchOption, selectedBranch, selectedLevel]);

  const handleLevelChange = (value) => {
    setValue("level", value, { shouldValidate: true });
    setBranchOption("");
    setCustomBranch("");
    setValue("branch", "", { shouldValidate: true });
    setTitleOption("");
    setCustomTitle("");
    setValue("title", "", { shouldValidate: true });
  };

  const handleBranchChange = (value) => {
    setBranchOption(value);
    setTitleOption("");
    setCustomTitle("");
    setValue("title", "", { shouldValidate: true });

    if (value === "otro") {
      setCustomBranch("");
      setValue("branch", "", { shouldValidate: true });
      return;
    }

    setCustomBranch("");
    setValue("branch", value, { shouldValidate: true });
  };

  const handleCustomBranchChange = (event) => {
    const value = event.target.value;
    setCustomBranch(value);
    setValue("branch", value, { shouldValidate: true });
  };

  const handleCustomBranchBlur = (event) => {
    const value = toTitleCase(event.target.value);
    setCustomBranch(value);
    setValue("branch", value, { shouldValidate: true });
  };

  const handleTitleChange = (value) => {
    setTitleOption(value);
    if (value === "otro") {
      setCustomTitle("");
      setValue("title", "", { shouldValidate: true });
      return;
    }
    setCustomTitle("");
    setValue("title", value, { shouldValidate: true });
  };

  const handleCustomTitleChange = (event) => {
    const value = event.target.value;
    setCustomTitle(value);
    setValue("title", value, { shouldValidate: true });
  };

  const handleCustomTitleBlur = (event) => {
    const value = toTitleCase(event.target.value);
    setCustomTitle(value);
    setValue("title", value, { shouldValidate: true });
  };

  const handleYearChange = (value) => {
    setYearOption(value);
    setValue("year", value, { shouldValidate: true });
  };

  const startAdd = () => {
    setIsAdding(true);
    setEditingEntryId(null);
    reset();
    setCertUrl(null);
    setBranchOption("");
    setCustomBranch("");
    setTitleOption("");
    setCustomTitle("");
    setYearOption("");
  };

  const startEdit = (entry) => {
    setIsAdding(true);
    setEditingEntryId(entry.id);
    setCertUrl(entry.certificate_url || null);
    const level = entry.level || "";
    const branch = entry.branch || "";
    const staticBranches = branchOptionsByLevel[level] || [];
    const allowedBranches = mergeUniqueOptions(staticBranches, dynamicBranchOptions);
    if (branch && allowedBranches.includes(branch)) {
      setBranchOption(branch);
      setCustomBranch("");
    } else if (branch) {
      setBranchOption("otro");
      setCustomBranch(branch);
    } else {
      setBranchOption("");
      setCustomBranch("");
    }
    setYearOption(entry.year || "");
    setValue("level", level, { shouldValidate: true });
    setValue("branch", branch, { shouldValidate: true });
    setValue("year", entry.year || "", { shouldValidate: true });

    const availableTitles = titleOptionsByLevelAndBranch[level]?.[branch] || baseTitleOptions;
    if (availableTitles.includes(entry.title)) {
      setTitleOption(entry.title);
      setCustomTitle("");
      setValue("title", entry.title, { shouldValidate: true });
    } else {
      setTitleOption("otro");
      setCustomTitle(entry.title || "");
      setValue("title", entry.title || "", { shouldValidate: true });
    }
  };

  const handleCertUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("El Archivo No Debe Superar Los 10MB");
      return;
    }

    setUploadingCert(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCertUrl(`${BACKEND_URL}${response.data.url}`);
      toast.success("Certificado Subido Correctamente");
    } catch (error) {
      console.error("Error uploading certificate:", error);
      toast.error("Error Al Subir El Certificado");
    } finally {
      setUploadingCert(false);
    }
  };

  const onSubmitEntry = async (data) => {
    if (!profileId) {
      toast.error("Primero Complete Los Datos Personales");
      return;
    }

    const certificateRequired = requiredCertificateLevels.has(data.level);
    if (certificateRequired && !certUrl) {
      toast.error("El Soporte Es Obligatorio");
      return;
    }

    setSubmitting(true);
    try {
      const entry = {
        ...data,
        certificate_url: certUrl,
      };

      const response = editingEntryId
        ? await axios.put(`${API}/profiles/${profileId}/academic/${editingEntryId}`, entry)
        : await axios.post(`${API}/profiles/${profileId}/academic`, entry);
      setEntries(response.data.academic_info);
      setProfile(response.data);
      setDynamicBranchOptions((prev) => mergeUniqueOptions(prev, [entry.branch]));
      setDynamicTitleOptions((prev) => mergeUniqueOptions(prev, [entry.title]));
      setIsAdding(false);
      reset();
      setCertUrl(null);
      setEditingEntryId(null);
      setBranchOption("");
      setCustomBranch("");
      setTitleOption("");
      setCustomTitle("");
      setYearOption("");
      toast.success(editingEntryId ? "Estudio Actualizado" : "Estudio Agregado Correctamente");
    } catch (error) {
      console.error("Error adding academic entry:", error);
      toast.error(editingEntryId ? "Error Al Actualizar El Estudio" : "Error Al Agregar El Estudio");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      const response = await axios.delete(`${API}/profiles/${profileId}/academic/${entryId}`);
      setEntries(response.data.academic_info);
      setProfile(response.data);
      toast.success("Estudio Eliminado");
    } catch (error) {
      console.error("Error deleting academic entry:", error);
      toast.error("Error Al Eliminar El Estudio");
    }
  };

  const cancelAdd = () => {
    setIsAdding(false);
    setEditingEntryId(null);
    reset();
    setCertUrl(null);
    setBranchOption("");
    setCustomBranch("");
    setTitleOption("");
    setCustomTitle("");
    setYearOption("");
  };

  const getLevelLabel = (value) => {
    return academicLevels.find((l) => l.value === value)?.label || value;
  };

  const missingRequiredCertificates = entries.some((entry) => !entry.certificate_url);

  const sortedEntries = [...entries].sort((a, b) => {
    const yearA = parseInt(a.year, 10) || 0;
    const yearB = parseInt(b.year, 10) || 0;
    return yearB - yearA;
  });

  const handleNext = () => {
    if (entries.length === 0) {
      toast.error("Agregue Al Menos Un Estudio");
      return;
    }
    if (missingRequiredCertificates) {
      toast.error("Todos Los Estudios Deben Tener Soporte");
      return;
    }
    onNext();
  };

  return (
    <div data-testid="academic-info-form">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-yellow-400 rounded flex items-center justify-center">
            <GraduationCap className="w-5 h-5 text-black" />
          </div>
          <h2 className="font-oswald text-2xl lg:text-3xl font-bold text-black tracking-wide">
            Información Académica
          </h2>
        </div>
        <p className="text-gray-500 ml-13">Agregue Sus Estudios Y Certificaciones</p>
      </div>

      {/* Existing Entries */}
      {entries.length > 0 && (
        <div className="space-y-4 mb-6">
          {sortedEntries.map((entry) => (
            <Card key={entry.id} className="entry-card border border-gray-200 rounded-sm">
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold tracking-wide text-yellow-600 bg-yellow-50 px-2 py-1 rounded">
                        {getLevelLabel(entry.level)}
                      </span>
                      <span className="text-sm text-gray-400">{entry.year}</span>
                    </div>
                    <h4 className="font-semibold text-gray-900">{entry.title}</h4>
                    {entry.institution && (
                      <p className="text-sm text-gray-600">{entry.institution}</p>
                    )}
                    {entry.certificate_url && (
                      <a
                        href={entry.certificate_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm text-blue-600 hover:underline mt-2"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        Ver Certificado
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-gray-400 hover:text-yellow-600"
                      onClick={() => startEdit(entry)}
                      data-testid={`edit-academic-${entry.id}`}
                    >
                      <Pencil className="w-5 h-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-gray-400 hover:text-red-500"
                      onClick={() => handleDeleteEntry(entry.id)}
                      data-testid={`delete-academic-${entry.id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add New Entry Form */}
      {isAdding ? (
        <Card className="border-2 border-yellow-400 rounded-sm mb-6">
          <CardContent className="p-6">
            <h3 className="font-oswald text-lg font-semibold mb-4">
              {editingEntryId ? "Editar Estudio" : "Nuevo Estudio"}
            </h3>
            <form onSubmit={handleSubmit(onSubmitEntry)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-field">
                  <Label className="text-sm font-medium text-gray-700 tracking-wide">
                    Nivel Académico
                  </Label>
                  <Select onValueChange={handleLevelChange} value={selectedLevel}>
                    <SelectTrigger className="mt-1 h-12 rounded-sm" data-testid="academic-level-select">
                      <SelectValue placeholder="Seleccione El Nivel" />
                    </SelectTrigger>
                    <SelectContent>
                      {academicLevels.map((level) => (
                        <SelectItem key={level.value} value={level.value}>
                          {level.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <input type="hidden" {...register("level")} />
                  {errors.level && (
                    <p className="text-red-500 text-sm mt-1">{errors.level.message}</p>
                  )}
                </div>


                <div className="form-field">
                  <Label className="text-sm font-medium text-gray-700 tracking-wide">
                    Rama
                  </Label>
                  <Select
                    value={branchOption || undefined}
                    onValueChange={handleBranchChange}
                    disabled={!isLevelSelected}
                  >
                    <SelectTrigger className="mt-1 h-12 rounded-sm" data-testid="academic-branch-select">
                      <SelectValue placeholder={branchPlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableBranchOptions.map((branch) => (
                        <SelectItem key={branch} value={branch}>
                          {branch}
                        </SelectItem>
                      ))}
                      <SelectItem value="otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                  {branchOption === "otro" && (
                    <Input
                      value={customBranch}
                      onChange={handleCustomBranchChange}
                      onBlur={handleCustomBranchBlur}
                      placeholder="Escriba La Rama"
                      className="mt-2 h-12 rounded-sm"
                      data-testid="academic-branch-custom-input"
                    />
                  )}
                  <input type="hidden" {...register("branch")} />
                  {errors.branch && (
                    <p className="text-red-500 text-sm mt-1">{errors.branch.message}</p>
                  )}
                </div>

                <div className="form-field">
                  <Label className="text-sm font-medium text-gray-700 tracking-wide">
                    Año De Graduación
                  </Label>
                  <Select value={yearOption || undefined} onValueChange={handleYearChange}>
                    <SelectTrigger className="mt-1 h-12 rounded-sm" data-testid="academic-year-select">
                      <SelectValue placeholder="Seleccione El Año" />
                    </SelectTrigger>
                    <SelectContent>
                      {graduationYears.map((year) => (
                        <SelectItem key={year} value={year}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <input type="hidden" {...register("year")} />
                  {errors.year && (
                    <p className="text-red-500 text-sm mt-1">{errors.year.message}</p>
                  )}
                </div>

                <div className="form-field md:col-span-2">
                  <Label className="text-sm font-medium text-gray-700 tracking-wide">
                    Título Obtenido
                  </Label>
                  <Select value={titleOption || undefined} onValueChange={handleTitleChange} disabled={!isBranchSelected}>
                    <SelectTrigger className="mt-1 h-12 rounded-sm" data-testid="academic-title-select">
                      <SelectValue placeholder={titlePlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTitleOptions.map((title) => (
                        <SelectItem key={title} value={title}>
                          {title}
                        </SelectItem>
                      ))}
                      <SelectItem value="otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                  {titleOption === "otro" && (
                    <Input
                      value={customTitle}
                      onChange={handleCustomTitleChange}
                      onBlur={handleCustomTitleBlur}
                      placeholder="Escriba El Título"
                      className="mt-2 h-12 rounded-sm"
                      data-testid="academic-title-custom-input"
                    />
                  )}
                  <input type="hidden" {...register("title")} />
                  {errors.title && (
                    <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
                  )}
                </div>

              </div>

              {/* Certificate Upload */}
              <div className="form-field">
                <Label className="text-sm font-medium text-gray-700 tracking-wide">
                  Soporte / Certificado
                </Label>
                {isCertificateRequired && (
                  <p className="text-xs text-gray-500 mt-1">
                    Obligatorio.
                  </p>
                )}
                <div
                  className="file-upload-zone mt-1 p-6 rounded-sm text-center cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="certificate-upload-zone"
                >
                  {certUrl ? (
                    <div className="flex items-center justify-center gap-2 text-green-600">
                      <FileText className="w-6 h-6" />
                      <span>Certificado Cargado</span>
                    </div>
                  ) : uploadingCert ? (
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-500">
                        Haga Clic Para Subir El Certificado
                      </p>
                      <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG (Máx. 10MB)</p>
                    </>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleCertUpload}
                  className="hidden"
                  data-testid="certificate-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={cancelAdd}
                  className="flex-1 h-12 rounded-sm tracking-wide"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-black text-yellow-400 hover:bg-gray-900 h-12 rounded-sm tracking-wide font-bold"
                  data-testid="add-academic-submit"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : editingEntryId ? "Guardar Cambios" : "Agregar"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={startAdd}
          className="w-full h-14 border-2 border-dashed border-gray-300 hover:border-yellow-400 rounded-sm mb-6"
          data-testid="add-academic-button"
        >
          <Plus className="w-5 h-5 mr-2" />
          Agregar Estudio
        </Button>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={onPrev}
          className="h-12 px-6 rounded-sm tracking-wide"
          data-testid="academic-prev-button"
        >
          <ChevronLeft className="w-5 h-5 mr-2" />
          Anterior
        </Button>
          <Button
            type="button"
            onClick={handleNext}
            disabled={loading}
            className="bg-black text-yellow-400 hover:bg-gray-900 hover:text-yellow-300 tracking-wide font-bold px-8 h-12 rounded-sm btn-primary"
            data-testid="academic-next-button"
          >
          Siguiente
          <ChevronRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </div>
  );
}
